import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { ArrowLeft, ArrowUpRight, ShoppingBag, Mail, Send, Loader2, X, Palette } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Footer } from "@/components/site/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
function mediaUrl(src) {
  if (!src) return "";
  return src.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${src}` : src;
}

const TopBar = () => (
  <header className="fixed top-0 left-0 right-0 z-[1000] bg-black/70 backdrop-blur-xl border-b border-white/10">
    <div className="flex items-center justify-between px-5 md:px-10 h-[68px]">
      <Link to="/" className="font-display text-2xl md:text-3xl tracking-tight leading-none"><span className="text-[#E30613]">VL</span><span className="text-white">MAG</span></Link>
      <Link to="/" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold text-neutral-400 hover:text-white transition-colors"><ArrowLeft size={16} /> Back To VLMAG</Link>
    </div>
  </header>
);

function InquiryModal({ piece, onClose }) {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [state, setState] = useState("");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const submit = async () => {
    if (!form.name || !form.email) { setState("error:Add your name and email."); return; }
    setState("sending");
    try {
      await axios.post(`${API}/art/inquire`, { piece_id: piece.id, piece_title: piece.title, ...form });
      setState("done");
    } catch { setState("error:Couldn't send. Try again."); }
  };
  const cls = "h-12 rounded-none bg-black/40 border-white/25 focus-visible:border-[#E30613] focus-visible:ring-0 focus-visible:ring-offset-0 text-white placeholder:text-neutral-600";
  return (
    <div className="fixed inset-0 z-[1200] flex items-end sm:items-center justify-center" data-testid="art-inquiry-modal">
      <div className="absolute inset-0 bg-black/75 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full sm:max-w-md bg-[#0a0a0a] border border-white/15 sm:mx-4">
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
          <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#E30613]">Request To Purchase</p>
          <button onClick={onClose} data-testid="art-inquiry-close" aria-label="Close" className="text-neutral-500 hover:text-white"><X size={20} /></button>
        </div>
        {state === "done" ? (
          <div className="px-6 py-10 text-center" data-testid="art-inquiry-done">
            <p className="font-display uppercase text-2xl text-white">Inquiry sent.</p>
            <p className="text-neutral-400 text-sm mt-3">Our team will reach out about <span className="text-white">{piece.title}</span> shortly.</p>
          </div>
        ) : (
          <div className="px-5 py-5">
            <p className="text-white font-bold">{piece.title}</p>
            <p className="text-[11px] uppercase tracking-[0.15em] font-bold text-neutral-500 mt-1">{piece.artist}{piece.price ? ` · ${piece.price}` : ""}</p>
            <div className="space-y-3 mt-4">
              <Input data-testid="art-inquiry-name" value={form.name} onChange={set("name")} placeholder="Your name" className={cls} />
              <Input data-testid="art-inquiry-email" value={form.email} onChange={set("email")} placeholder="Email" className={cls} />
              <textarea data-testid="art-inquiry-message" value={form.message} onChange={set("message")} rows={3} placeholder="Message (optional)" className="w-full rounded-none bg-black/40 border border-white/25 focus:border-[#E30613] focus:outline-none text-white placeholder:text-neutral-600 p-3 text-sm" />
            </div>
            {state.startsWith("error") && <p className="text-[#E30613] text-sm mt-3">{state.slice(6)}</p>}
            <button onClick={submit} disabled={state === "sending"} data-testid="art-inquiry-submit" className="mt-4 w-full inline-flex items-center justify-center gap-2 h-12 text-[10px] uppercase tracking-[0.2em] font-bold bg-[#E30613] text-white hover:bg-[#c00510] transition-colors disabled:opacity-60">
              {state === "sending" ? <Loader2 className="animate-spin" size={14} /> : <Mail size={14} />} Send Inquiry
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function SubmitArt() {
  const [form, setForm] = useState({ artist: "", email: "", artist_handle: "", medium: "", portfolio_url: "", note: "" });
  const [state, setState] = useState("");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const submit = async () => {
    if (!form.artist || !form.email || !form.portfolio_url) { setState("error:Add your name, email and a portfolio link."); return; }
    setState("sending");
    try { await axios.post(`${API}/art/submit`, form); setState("done"); }
    catch { setState("error:Couldn't send. Try again."); }
  };
  const cls = "h-12 rounded-none bg-black/40 border-white/25 focus-visible:border-[#E30613] focus-visible:ring-0 focus-visible:ring-offset-0 text-white placeholder:text-neutral-600";
  if (state === "done") return (
    <div className="border border-[#E30613] bg-[#E30613]/10 p-8 text-center" data-testid="art-submit-done">
      <p className="font-display uppercase text-2xl text-white">Portfolio received.</p>
      <p className="text-neutral-400 text-sm mt-3">Our curators review every submission. If it's a fit, you'll hear from us.</p>
    </div>
  );
  return (
    <div className="border border-white/10 p-6 md:p-8" data-testid="art-submit-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Input data-testid="art-submit-artist" value={form.artist} onChange={set("artist")} placeholder="Artist name" className={cls} />
        <Input data-testid="art-submit-email" value={form.email} onChange={set("email")} placeholder="Email" className={cls} />
        <Input data-testid="art-submit-ig" value={form.artist_handle} onChange={set("artist_handle")} placeholder="Instagram @handle" className={cls} />
        <Input data-testid="art-submit-medium" value={form.medium} onChange={set("medium")} placeholder="Medium (painting, photo, sculpture...)" className={cls} />
      </div>
      <Input data-testid="art-submit-portfolio" value={form.portfolio_url} onChange={set("portfolio_url")} placeholder="Portfolio link (website, Instagram, Drive...)" className={`${cls} mt-3`} />
      <textarea data-testid="art-submit-note" value={form.note} onChange={set("note")} rows={3} placeholder="Tell us about your work (optional)" className="w-full rounded-none bg-black/40 border border-white/25 focus:border-[#E30613] focus:outline-none text-white placeholder:text-neutral-600 p-3 text-sm mt-3" />
      {state.startsWith("error") && <p className="text-[#E30613] text-sm mt-3">{state.slice(6)}</p>}
      <button onClick={submit} disabled={state === "sending"} data-testid="art-submit-btn" className="mt-4 inline-flex items-center gap-2 px-6 h-12 text-[10px] uppercase tracking-[0.2em] font-bold bg-[#E30613] text-white hover:bg-[#c00510] transition-colors disabled:opacity-60">
        {state === "sending" ? <Loader2 className="animate-spin" size={14} /> : <Send size={14} />} Submit Your Art
      </button>
    </div>
  );
}

export default function ArtPage() {
  const [pieces, setPieces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inquiry, setInquiry] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    axios.get(`${API}/art/pieces`).then((r) => setPieces(r.data || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const featured = useMemo(() => pieces.find((p) => p.featured) || pieces[0], [pieces]);
  const gallery = useMemo(() => pieces.filter((p) => !featured || p.id !== featured.id), [pieces, featured]);
  const artists = useMemo(() => {
    const seen = new Map();
    pieces.forEach((p) => { if (p.artist && !seen.has(p.artist)) seen.set(p.artist, { artist: p.artist, handle: p.artist_handle, image: p.image, medium: p.medium }); });
    return [...seen.values()];
  }, [pieces]);

  const PieceActions = ({ p, size }) => (
    <div className={`flex flex-wrap items-center gap-3 ${size === "lg" ? "mt-6" : "mt-4"}`}>
      {p.for_sale && p.buy_url && (
        <a href={p.buy_url} target="_blank" rel="noopener noreferrer" data-testid={`art-buy-${p.id}`} className="inline-flex items-center gap-2 px-5 h-11 text-[10px] uppercase tracking-[0.2em] font-bold bg-white text-black hover:bg-neutral-200 transition-colors"><ShoppingBag size={14} /> Buy Now</a>
      )}
      {p.for_sale ? (
        <button onClick={() => setInquiry(p)} data-testid={`art-inquire-${p.id}`} className="inline-flex items-center gap-2 px-5 h-11 text-[10px] uppercase tracking-[0.2em] font-bold border border-[#E30613] text-white hover:bg-[#E30613] transition-colors"><Mail size={14} /> Inquire To Purchase</button>
      ) : (
        <span className="inline-flex items-center px-5 h-11 text-[10px] uppercase tracking-[0.2em] font-bold border border-white/15 text-neutral-500">Not For Sale</span>
      )}
    </div>
  );

  return (
    <div className="bg-black text-white min-h-screen" data-testid="art-page">
      <TopBar />
      <section className="px-5 md:px-10 pt-[108px] md:pt-[132px] pb-10">
        <p className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] font-bold text-[#E30613] mb-4"><Palette size={14} /> The Gallery</p>
        <h1 className="font-display uppercase leading-[0.85] tracking-tight text-5xl sm:text-6xl md:text-8xl">VLMAG<br />Art</h1>
        <p className="text-lg text-neutral-300 mt-6 max-w-2xl">The Canvas of the Culture — discover artists, collect exclusive pieces, and connect with the creatives shaping the art world.</p>
      </section>

      {loading ? (
        <div className="flex justify-center py-24"><Loader2 className="animate-spin text-[#E30613]" size={28} /></div>
      ) : (
        <>
          {featured && (
            <section className="px-5 md:px-10 py-10 border-t border-white/10" data-testid="art-featured">
              <p className="text-xs uppercase tracking-[0.25em] font-bold text-[#E30613] mb-6">Featured Piece</p>
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-10 items-center">
                <div className="lg:col-span-7">
                  <div className="relative overflow-hidden border border-white/10">
                    <img src={mediaUrl(featured.image)} alt={featured.title} className="w-full h-full object-cover" />
                    {featured.edition && <span className="absolute top-0 left-0 bg-[#E30613] px-3 py-1.5 text-[9px] uppercase tracking-[0.18em] font-bold text-white">{featured.edition}</span>}
                  </div>
                </div>
                <div className="lg:col-span-5">
                  <h2 className="font-display uppercase text-4xl md:text-6xl tracking-tight leading-[0.9]">{featured.title}</h2>
                  <p className="text-neutral-300 mt-4 text-lg">{featured.artist}</p>
                  <p className="text-sm text-neutral-500 mt-1">{[featured.medium, featured.year].filter(Boolean).join(" · ")}</p>
                  {featured.description && <p className="text-neutral-400 mt-5 leading-relaxed">{featured.description}</p>}
                  {featured.price && <p className="font-display text-3xl text-white mt-5">{featured.price}</p>}
                  <PieceActions p={featured} size="lg" />
                </div>
              </div>
            </section>
          )}

          <section className="px-5 md:px-10 py-12 border-t border-white/10">
            <h2 className="font-display uppercase text-3xl md:text-5xl tracking-tight leading-none mb-8">The Collection</h2>
            {gallery.length === 0 ? (
              <p className="text-neutral-600 py-16 text-center border border-white/10">More pieces dropping soon.</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8" data-testid="art-grid">
                {gallery.map((p, i) => (
                  <motion.article key={p.id} data-testid={`art-piece-${p.id}`} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-50px" }} transition={{ duration: 0.6, delay: (i % 3) * 0.08 }} className="group border border-white/10 hover:border-[#E30613] transition-colors duration-500">
                    <div className="relative aspect-square overflow-hidden">
                      <img src={mediaUrl(p.image)} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                      {p.edition && <span className="absolute top-0 left-0 bg-black px-3 py-1.5 text-[9px] uppercase tracking-[0.18em] font-bold text-[#E30613] border-r border-b border-white/10">{p.edition}</span>}
                    </div>
                    <div className="p-5">
                      <h3 className="font-display uppercase text-xl leading-tight tracking-tight text-white group-hover:text-[#E30613] transition-colors">{p.title}</h3>
                      <p className="text-[11px] uppercase tracking-[0.15em] font-bold text-neutral-500 mt-2">{p.artist}</p>
                      <p className="text-xs text-neutral-600 mt-1">{[p.medium, p.year].filter(Boolean).join(" · ")}</p>
                      {p.price && <p className="font-display text-2xl text-white mt-3">{p.price}</p>}
                      <PieceActions p={p} />
                    </div>
                  </motion.article>
                ))}
              </div>
            )}
          </section>

          {artists.length > 0 && (
            <section className="px-5 md:px-10 py-12 border-t border-white/10" data-testid="art-artists">
              <h2 className="font-display uppercase text-3xl md:text-5xl tracking-tight leading-none mb-8">Discover Artists</h2>
              <div className="flex flex-wrap gap-6">
                {artists.map((a) => (
                  <a key={a.artist} href={a.handle ? `https://instagram.com/${a.handle.replace("@", "")}` : undefined} target="_blank" rel="noopener noreferrer" className="group flex items-center gap-4">
                    <div className="w-16 h-16 rounded-full overflow-hidden border border-white/15 shrink-0">
                      <img src={mediaUrl(a.image)} alt={a.artist} className="w-full h-full object-cover grayscale-img group-hover:scale-105 transition-transform" />
                    </div>
                    <div>
                      <p className="text-white font-bold group-hover:text-[#E30613] transition-colors">{a.artist}</p>
                      <p className="text-[11px] uppercase tracking-[0.15em] font-bold text-neutral-500">{a.medium || (a.handle ? a.handle : "Artist")}</p>
                    </div>
                  </a>
                ))}
              </div>
            </section>
          )}
        </>
      )}

      <section className="px-5 md:px-10 py-16 md:py-24 border-t border-white/10">
        <div className="max-w-3xl">
          <p className="text-xs uppercase tracking-[0.25em] font-bold text-[#E30613] mb-4">For The Creatives</p>
          <h2 className="font-display uppercase text-4xl md:text-6xl tracking-tight leading-[0.9] mb-4">Submit Your Art</h2>
          <p className="text-neutral-400 mb-8 max-w-xl">Painter, photographer, sculptor, digital artist — pitch your work to be featured and sold through the VLMAG gallery.</p>
          <SubmitArt />
        </div>
      </section>

      <AnimatePresence>{inquiry && <InquiryModal piece={inquiry} onClose={() => setInquiry(null)} />}</AnimatePresence>
      <Footer />
    </div>
  );
}
